/**
 * Clase `Finva` — implementación R1 (contrato §3.2).
 *
 * El SDK NO usa una llave pública: el comercio se identifica por el `host`
 * (origin de la página que embebe el widget). Se auto-detecta desde
 * `window.location.origin` y viaja al iframe como `?host=`; el server resuelve
 * el tenant (holding/whatsapp/asesor) a partir de ese origin. Se puede
 * overridear con `options.host` (útil para pruebas/SSR donde no hay `window`).
 *
 * `origin` (dónde vive el iframe `/aplicacion` y `/api/sdk/config`) se
 * auto-detecta del `<script src>` que cargó este bundle: un comercio que
 * carga `<script src="https://finva-app/sdk/v1/finva.js">` y hace
 * `new Finva({ locale })` ya queda cableado al dominio correcto sin pasar
 * `origin`. Orden de resolución: `options.origin` → origin del script →
 * `DEFAULT_ORIGIN`. Overridear `origin` sólo para dev/staging.
 *
 * `bricks()` devuelve un builder ligado a esta instancia (host, locale,
 * origin). `open()/close()` operan sobre el último brick modal registrado.
 * Mientras no haya un brick modal, `open()/close()` son no-op con un aviso por
 * consola: no lanzan, para no romper snippets que ya los llamen en embedded.
 */
import type { FinvaOptions } from './api';
import type { Bricks } from './bricks';
import { createBricks, type ModalBrickHandle } from './bricks';

const DEFAULT_LOCALE = 'es-MX';
const DEFAULT_ORIGIN = 'https://app.finva.mx';

/**
 * Origin del propio bundle del SDK, deducido del `<script src>` que lo cargó.
 * Se captura al evaluar el módulo (durante la ejecución del IIFE), cuando
 * `document.currentScript` apunta al script en ejecución — por eso es una
 * constante de módulo y no se lee perezosamente (`document.currentScript` ya
 * sería `null` cuando el comercio llame `new Finva()` más tarde).
 *
 * '' si no se pudo determinar (SSR, module loader, jsdom): en ese caso el
 * constructor cae a `DEFAULT_ORIGIN`.
 */
const SDK_SCRIPT_ORIGIN = detectScriptOrigin();

export class Finva {
  /** Origin de la página host (identifica al tenant). '' si no se pudo detectar. */
  readonly host: string;
  readonly locale: string;
  readonly origin: string;

  /** Último brick modal registrado (lo popula R2 en Ola 2). */
  private lastModal: ModalBrickHandle | null = null;

  constructor(options?: FinvaOptions) {
    this.host = (options?.host?.trim() || detectHost()).replace(/\/$/, '');
    this.locale = options?.locale?.trim() || DEFAULT_LOCALE;
    this.origin =
      options?.origin?.trim().replace(/\/$/, '') || SDK_SCRIPT_ORIGIN || DEFAULT_ORIGIN;
  }

  bricks(): Bricks {
    return createBricks(this);
  }

  /** Azúcar: abre el último brick modal creado. No-op si no hay modal brick. */
  open(): void {
    if (!this.lastModal) {
      warnNoModal('open');
      return;
    }
    this.lastModal.open();
  }

  /** Azúcar: cierra el último brick modal creado. No-op si no hay modal brick. */
  close(): void {
    if (!this.lastModal) {
      warnNoModal('close');
      return;
    }
    this.lastModal.close();
  }

  /**
   * Registro interno usado por el builder de bricks (R2 en Ola 2 registrará su
   * controller modal aquí). R1 no registra nada (modo embedded no aplica).
   * Visible dentro del paquete pero no parte del contrato público.
   */
  _registerModalBrick(handle: ModalBrickHandle | null): void {
    this.lastModal = handle;
  }
}

/** Origin de la página donde corre el SDK. '' si no hay `window` (SSR/tests). */
function detectHost(): string {
  if (typeof window === 'undefined' || !window.location) return '';
  return window.location.origin;
}

/**
 * Origin del `<script>` que cargó el bundle (de dónde se sirve el SDK = dónde
 * vive `/aplicacion`). '' si no hay `document` o no hay `currentScript`
 * (module loader/SSR/jsdom). Ver `SDK_SCRIPT_ORIGIN` para por qué se captura
 * al evaluar el módulo.
 */
function detectScriptOrigin(): string {
  if (typeof document === 'undefined') return '';
  const src = (document.currentScript as HTMLScriptElement | null)?.src;
  if (!src) return '';
  try {
    return new URL(src).origin;
  } catch {
    return '';
  }
}

function warnNoModal(op: 'open' | 'close'): void {
  // eslint-disable-next-line no-console
  console.warn(`Finva.${op}(): no hay brick modal registrado (modo modal se implementa en Ola 2).`);
}
